// Generic two-column side-by-side diff renderer.
// Accepts pre-formatted text strings (before/after).
// Mirrors VersionDiff.tsx but domain-agnostic — no frontmatter/body splitting.

import { diffLines } from "diff";

interface GenericVersionDiffProps {
  /** The "before" text (older / selected history version). */
  beforeText: string;
  /** The "after" text (current editor state or latest saved). */
  afterText: string;
}

export function GenericVersionDiff({ beforeText, afterText }: GenericVersionDiffProps) {
  const changes = diffLines(beforeText, afterText);

  const leftLines: Array<{ text: string; kind: "unchanged" | "removed" | "added" }> = [];
  const rightLines: Array<{ text: string; kind: "unchanged" | "removed" | "added" }> = [];

  for (const change of changes) {
    const lines = (change.value.endsWith("\n") ? change.value.slice(0, -1) : change.value).split(
      "\n"
    );

    if (change.added) {
      for (const line of lines) {
        leftLines.push({ text: " ", kind: "unchanged" });
        rightLines.push({ text: line, kind: "added" });
      }
    } else if (change.removed) {
      for (const line of lines) {
        leftLines.push({ text: line, kind: "removed" });
        rightLines.push({ text: " ", kind: "unchanged" });
      }
    } else {
      for (const line of lines) {
        leftLines.push({ text: line, kind: "unchanged" });
        rightLines.push({ text: line, kind: "unchanged" });
      }
    }
  }

  return (
    <div className="flex gap-2 overflow-auto">
      <DiffColumn lines={leftLines} label="Before" />
      <DiffColumn lines={rightLines} label="After" />
    </div>
  );
}

interface DiffColumnProps {
  lines: Array<{ text: string; kind: "unchanged" | "removed" | "added" }>;
  label: string;
}

function DiffColumn({ lines, label }: DiffColumnProps) {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground mb-1 font-medium">{label}</p>
      <pre className="font-mono text-xs overflow-auto rounded border border-border p-2 bg-muted/20 max-h-[360px]">
        {lines.map((line, i) => (
          <div
            key={i}
            className={
              line.kind === "removed"
                ? "bg-destructive/10 text-destructive line-through"
                : line.kind === "added"
                  ? "bg-success/10 text-success"
                  : undefined
            }
          >
            {line.text}
          </div>
        ))}
      </pre>
    </div>
  );
}
